/**
 * The React side of the live estimate: debounce, hand it to a worker, and never
 * show an answer to a question that has since changed.
 *
 * Three rules, all of them learned from the alternative:
 *
 *   • DEBOUNCE. The request is rebuilt on every keystroke; running the
 *     assessment on every keystroke would queue a second of work behind each
 *     one and the card would spend its life a dozen edits behind.
 *   • SIGNATURE, not identity. `requestSignature` covers everything that can
 *     move a number and nothing else, so renaming a piece — by far the most
 *     common edit on this screen — costs nothing at all.
 *   • TOKENS. Answers arrive out of order once a run is abandoned mid-flight;
 *     an answer whose token is not the one being waited for is dropped rather
 *     than rendered as the current set's consumption.
 *
 * The worker is created lazily and torn down with the screen. A browser that
 * refuses to give us one (or a worker that dies) falls back to computing in an
 * idle callback on the main thread: slower and jankier, but the card still
 * carries a number, which is the whole reason it exists.
 *
 * ONE listener, and a ref naming the run it is for. The obvious alternative —
 * a listener per postMessage that unsubscribes on the first message it sees —
 * looks equivalent and is not: two runs in flight means two listeners, the
 * older answer wakes BOTH of them, both unsubscribe, and the newer run's answer
 * then arrives to an empty worker with nobody listening. The card sits on
 * "hesaplanıyor" until the operator happens to edit something else. So the
 * subscription belongs to the WORKER and the token decides whose answer it is.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  computeEstimate,
  createEstimateCache,
  requestSignature,
  type EstimateAnswer,
  type EstimateCache,
  type EstimateJob,
  type EstimateRequest,
  type EstimateResult,
} from './live-calc'

/** Long enough that a typed word is one run, short enough to feel live. */
export const ESTIMATE_DEBOUNCE_MS = 300

/** A run handed to the worker, and everything needed to finish it elsewhere. */
interface PendingJob {
  token: number
  request: EstimateRequest
  /** False once the screen has moved on — checked before spending a fallback run. */
  alive: () => boolean
  settle: (answer: EstimateAnswer) => void
}

export interface LiveEstimate {
  /** The last answer that is still valid, or null before the first one. */
  result: EstimateResult | null
  /** True while the numbers on screen are older than the set on screen. */
  pending: boolean
  /** Set when the assessment itself failed — the card says so rather than lying. */
  error: string | null
}

export function useLiveEstimate(request: EstimateRequest | null): LiveEstimate {
  const signature = useMemo(() => (request ? requestSignature(request) : ''), [request])
  const [answered, setAnswered] = useState<{ signature: string; result: EstimateResult } | null>(
    null,
  )
  // Keyed by signature like the answer itself, so a failure never outlives the
  // set it was about: change the piece that could not be assessed and the card
  // goes back to computing instead of showing yesterday's complaint.
  const [failure, setFailure] = useState<{ signature: string; message: string } | null>(null)

  // The request itself is deliberately NOT a dependency: it is a new object on
  // every render, and its signature is what decides whether the work is stale.
  const latest = useRef<EstimateRequest | null>(request)
  latest.current = request

  const workerRef = useRef<Worker | null>(null)
  const workerFailed = useRef(false)
  const fallbackCache = useRef<EstimateCache | null>(null)
  const pending = useRef<PendingJob | null>(null)
  const token = useRef(0)

  useEffect(
    () => () => {
      workerRef.current?.terminate()
      workerRef.current = null
      pending.current = null
    },
    [],
  )

  const empty = request === null || request.pieces.length === 0
  const current = empty ? null : answered?.signature === signature ? answered.result : null
  const error = empty || failure?.signature !== signature ? null : failure.message

  useEffect(() => {
    if (empty) return
    if (answered?.signature === signature) return

    let live = true
    const timer = window.setTimeout(() => {
      const asked = latest.current
      if (!live || !asked) return
      token.current += 1
      const jobToken = token.current

      const job: PendingJob = {
        token: jobToken,
        request: asked,
        alive: () => live,
        settle: (answer) => {
          if (!live || answer.token !== jobToken) return
          if (answer.result) {
            setAnswered({ signature, result: answer.result })
            setFailure(null)
          } else {
            setFailure({ signature, message: answer.error ?? 'Hesaplanamadı.' })
          }
        },
      }

      const worker = workerFailed.current
        ? null
        : ensureWorker(workerRef, workerFailed, pending, fallbackCache)
      if (worker) {
        // The previous run, if any, is forgotten here rather than cancelled:
        // the worker is single-threaded, its answer will still arrive, and the
        // token on it will no longer be the one anybody is waiting for.
        pending.current = job
        const message: EstimateJob = { token: job.token, request: job.request }
        worker.postMessage(message)
        return
      }

      // No worker: compute on the main thread, but wait for a gap first so the
      // keystroke that triggered this has already been painted.
      runOnMainThread(job, fallbackCache)
    }, ESTIMATE_DEBOUNCE_MS)

    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [answered?.signature, empty, signature])

  return { result: current, pending: !empty && current === null && error === null, error }
}

/**
 * One worker per screen, made on first use, with ONE message subscription.
 *
 * `new URL(..., import.meta.url)` is the form the bundler recognises, so the
 * worker is emitted as its own chunk rather than inlined into the page. A
 * browser that cannot create it, or one whose worker dies mid-run, marks the
 * whole path failed and never tries again for the life of the screen — the
 * fallback is slower, but a card that retries a broken worker every 300 ms is
 * worse than a card that stopped asking.
 *
 * A worker that dies hands its unfinished run to the main thread instead of
 * dropping it. The card is otherwise left saying "hesaplanıyor" about a run
 * whose only listener has just been terminated, which is the same dead end the
 * one-listener rule above exists to close.
 */
function ensureWorker(
  ref: { current: Worker | null },
  failed: { current: boolean },
  pending: { current: PendingJob | null },
  fallbackCache: { current: EstimateCache | null },
): Worker | null {
  if (ref.current) return ref.current
  try {
    const worker = new Worker(new URL('./live-calc.worker.ts', import.meta.url), {
      type: 'module',
    })
    worker.addEventListener('message', (event: MessageEvent<EstimateAnswer>) => {
      const waiting = pending.current
      // An answer to a run nobody waits for any more: the set moved on while
      // the worker was busy, and a newer job is already in the queue behind it.
      if (!waiting || event.data.token !== waiting.token) return
      pending.current = null
      waiting.settle(event.data)
    })
    worker.addEventListener('error', () => {
      failed.current = true
      worker.terminate()
      if (ref.current === worker) ref.current = null
      const waiting = pending.current
      pending.current = null
      if (waiting) runOnMainThread(waiting, fallbackCache)
    })
    ref.current = worker
    return worker
  } catch {
    failed.current = true
    return null
  }
}

/** The fallback path: same computation, same cache, on this thread. */
function runOnMainThread(
  job: PendingJob,
  fallbackCache: { current: EstimateCache | null },
): void {
  runWhenIdle(() => {
    if (!job.alive()) return
    fallbackCache.current ??= createEstimateCache()
    try {
      job.settle({
        token: job.token,
        result: computeEstimate(job.request, fallbackCache.current),
        error: null,
      })
    } catch (thrown: unknown) {
      job.settle({
        token: job.token,
        result: null,
        error: thrown instanceof Error ? thrown.message : 'Hesaplanamadı.',
      })
    }
  })
}

function runWhenIdle(work: () => void): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => work(), { timeout: 1_000 })
    return
  }
  window.setTimeout(work, 0)
}
